import Check from '@mui/icons-material/Check';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import { useQueryClient } from '@tanstack/react-query';
import { t } from 'i18next';
import { useCallback, useContext, useMemo, useState } from 'react';

import {
  BulkActionsBar as BulkActionsBarComponent,
  BulkActionsBarButton,
  BulkActionsBarCountButton,
} from '~components/BulkActionsBar/BulkActionsBar';
import { trackApprovalBatch } from '~hooks/useAnalytics/trackApprovals';
import { Job } from '~hooks/useJob/models';
import { useJob } from '~hooks/useJob/useJob';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import FilesPageContext from '../../FilesPageContext';
import { makeJobsQueryContextKey } from '../../utils';
import DownloadFilesDialog from '../DownloadFilesDialog/DownloadFilesDialog';

export default function BulkActionsBar() {
  const { checkedJobIds, approvableJobs, uncheckAllJobs } = useContext(FilesPageContext);

  const { approveJob } = useJob();
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const queryClient = useQueryClient();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isApprovingAllJobs, setIsApprovingAllJobs] = useState(false);

  const checkedJobsCount = checkedJobIds.length;
  const isVisible = checkedJobsCount > 0;
  const checkedApprovableJobs = useMemo(() => {
    return approvableJobs.filter((approvableJob) => {
      return checkedJobIds.includes(approvableJob.id);
    });
  }, [checkedJobIds, approvableJobs]);

  const hasCheckedApprovableJobs = checkedApprovableJobs.length > 0;

  const handleApproveAllJobsClick = useCallback(async () => {
    try {
      setIsApprovingAllJobs(true);

      const approvedJobs = await Promise.all(
        checkedApprovableJobs.map((job) =>
          approveJob(job.id, job.company?.id ?? companyId),
        ),
      );
      const approvedCount = approvedJobs.filter(Boolean).length;

      if (approvedCount > 0) {
        trackApprovalBatch('job', approvedCount);
      }

      approvedJobs.forEach((job) => {
        if (job) {
          const queryKey = makeJobsQueryContextKey(String(job.order?.id));

          queryClient.setQueryData([queryKey], (oldJobs: Job[]) => {
            const oldJobIndex = oldJobs.findIndex((j) => j.id === job.id);

            if (oldJobIndex >= 0) {
              const newJobs = [...oldJobs];
              newJobs.splice(oldJobIndex, 1, job);

              return newJobs;
            }

            return oldJobs;
          });
        }
      });

      setIsApprovingAllJobs(false);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.jobs.failed_to_approve_job'),
      );
    }
  }, [checkedApprovableJobs, companyId]);

  if (!isVisible) {
    return null;
  }

  return (
    <>
      <BulkActionsBarComponent>
        <BulkActionsBarCountButton onClick={uncheckAllJobs}>
          {t('common.count_jobs_selected', {
            count: checkedJobsCount,
            plural: checkedJobsCount > 1 ? 's' : '',
          })}
        </BulkActionsBarCountButton>

        <BulkActionsBarButton
          color="inherit"
          onClick={() => setIsDialogOpen(true)}
          startIcon={<FileDownloadOutlined />}
        >
          {t('approvals.files.download_files_dialog.title')}
        </BulkActionsBarButton>

        <BulkActionsBarButton
          color="success"
          disabled={!hasCheckedApprovableJobs}
          loading={isApprovingAllJobs}
          loadingPosition="start"
          onClick={handleApproveAllJobsClick}
          startIcon={<Check />}
        >
          {t('approvals.files.bulk_actions_bar.approve_all_jobs_cta')}
        </BulkActionsBarButton>
      </BulkActionsBarComponent>

      <DownloadFilesDialog
        isOpen={isDialogOpen}
        jobIds={checkedJobIds}
        onClose={() => setIsDialogOpen(false)}
      />
    </>
  );
}
